import React, {useState, useRef, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import useOnEscape from 'lib/hooks/useOnEscape';
import {Button, Icon, Text, WithTooltip} from '@albert-io/atomic';

import './select-dropdown.scss';

/**
 * @todo Refactor to 1. accept value in addition to defaultValue
 * to behave more like a controlled component and 2. update trigger
 * pattern to something more open and extensible
 */

const ButtonSelect = ({label, selected, ...rest}) => (
  <Button
    type='button'
    variant='outlined'
    className='select-dropdown__listbox-button u-width_100pc u-justify-content_space-between'
    {...rest}
  >
    <Text color='inherit' bold>
      {selected?.label || selected?.value || label}
    </Text>
    <Icon
      size='standalone'
      className='select-dropdown__listbox-icon u-mar-l_1'
      color='inherit'
      icon='caret-down'
    />
  </Button>
);

ButtonSelect.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.object
};

const SelectDropdown = ({
  className,
  label,
  options,
  onSelect,
  disabled,
  trigger,
  isSelected,
  changeEvent,
  defaultValue,
  onClose,
  isOpen,
  setIsOpen
}) => {
  const ref = useRef();

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const defaultOption = options.find((item) => item.value === defaultValue);
    if (defaultValue && options.length > 0) {
      setSelected(defaultOption);
    }
  }, [options, defaultValue]);

  const handleCallback = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  useOnClickOutside(ref, handleCallback);

  useOnEscape(ref, handleCallback);

  const handleIsSelected = (option) => {
    if (isSelected) {
      return isSelected(option);
    }

    return selected?.id === option.id;
  };

  const sharedProps = {
    label,
    selected,
    disabled,
    [changeEvent]: () => !disabled && setIsOpen(true),
    'aria-haspopup': 'listbox',
    'aria-label': label
  };

  return (
    <div ref={ref} className={classnames('select-dropdown', className)}>
      {React.cloneElement(trigger, sharedProps)}
      <ul
        className={classnames('select-dropdown__listbox', {
          'select-dropdown__listbox--open': isOpen
        })}
        role='listbox'
        tabIndex='-1'
      >
        {options.map((option) => (
          <Option
            key={option.id}
            onClick={
              option.onClick
                ? option.onClick
                : () => {
                    setIsOpen(false);
                    setSelected(option);
                    onSelect(option);
                  }
            }
            selected={handleIsSelected(option)}
            setIsOpen={setIsOpen}
            {...option}
          />
        ))}
      </ul>
    </div>
  );
};

SelectDropdown.propTypes = {
  className: PropTypes.string,
  changeEvent: PropTypes.string,
  trigger: PropTypes.node,
  isSelected: PropTypes.func,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func
};

SelectDropdown.defaultProps = {
  changeEvent: 'onClick',
  options: [],
  disabled: false,
  trigger: <ButtonSelect />,
  onSelect: () => {},
  onClose: () => {}
};

const Option = ({label, value, tooltip, disabled, selected, onClick, setIsOpen, icon}) => {
  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter' && !disabled) {
      onClick(e);
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <li
      className={classnames('u-pad_2 select-dropdown__option', {
        'select-dropdown__option--selected': selected,
        'select-dropdown__option--disabled': disabled
      })}
      aria-selected={selected}
      role='option'
      onClick={!disabled ? onClick : null}
      onKeyDown={handleOnKeyDown}
      disabled={disabled}
      tabIndex='0'
    >
      <Text bold emphasis='medium'>
        {icon && <Icon className='u-mar-r_2' icon={icon} iconStyle='regular' emphasis='lowest' />}
        {label || value}
      </Text>
      {tooltip && disabled && (
        <WithTooltip
          enabled
          content={tooltip}
          placement='right'
          className='select-dropdown__tooltip'
          anchoredFixedPositionProps={{
            modifiers: {
              preventOverflow: {
                boundariesElement: 'window'
              }
            }
          }}
        >
          <Icon icon='info-circle' iconStyle='regular' color='black' emphasis='lowest' />
        </WithTooltip>
      )}
      {selected && !disabled && (
        <Icon className='u-mar-l_2' icon='check' iconStyle='regular' emphases='lowest' />
      )}
    </li>
  );
};

Option.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  setIsOpen: PropTypes.func,
  icon: PropTypes.string
};

export default SelectDropdown;
